import { createSelector } from '@reduxjs/toolkit';
import { QueryPreviewTabs, } from './types.js';
import { queryComparators } from './utils/comparators.js';
import { queryListFilters } from './utils/filters.js';
import { emptyRecord } from './utils/object.js';
import { escapeRegExpSpecialCharacter } from './utils/regexp.js';
import { getApiStatesOf, extractAllApiQueries, flipComparator, getQueryTagsOf, generateApiStatsOfCurrentQuery, getActionsOfCurrentQuery, extractAllApiMutations, } from './utils/rtk-query.js';
export function computeSelectorSource(props, previous = null) {
    const { computedStates, currentStateIndex, monitorState, actionsById } = props;
    const userState = computedStates.length > 0 ? computedStates[currentStateIndex].state : null;
    if (!previous ||
        previous.userState !== userState ||
        previous.monitorState !== monitorState ||
        previous.actionsById !== actionsById) {
        return {
            userState,
            monitorState,
            currentStateIndex,
            actionsById,
        };
    }
    return previous;
}
export function createInspectorSelectors() {
    const selectQueryComparator = ({ monitorState, }) => {
        return queryComparators[monitorState.queryForm.values.queryComparator];
    };
    const selectQueryListFilter = ({ monitorState, }) => {
        return queryListFilters[monitorState.queryForm.values.queryFilter];
    };
    const selectActionsById = ({ actionsById }) => actionsById;
    const selectApiStates = createSelector(({ userState }) => userState, getApiStatesOf);
    const selectAllQueries = createSelector(selectApiStates, extractAllApiQueries);
    const selectAllMutations = createSelector(selectApiStates, extractAllApiMutations);
    const selectSearchQueryRegex = createSelector(({ monitorState }) => monitorState.queryForm.values.searchValue, ({ monitorState }) => monitorState.queryForm.values.isRegexSearch, (searchValue, isRegexSearch) => {
        if (searchValue) {
            try {
                const regexPattern = isRegexSearch
                    ? searchValue
                    : escapeRegExpSpecialCharacter(searchValue);
                return new RegExp(regexPattern, 'i');
            }
            catch (err) {
                // We notify that the search regex provided is not valid
            }
        }
        return null;
    });
    const selectComparatorOrder = ({ monitorState }) => monitorState.queryForm.values.isAscendingQueryComparatorOrder;
    const selectAllVisbileQueries = createSelector([
        selectQueryComparator,
        selectQueryListFilter,
        selectAllQueries,
        selectAllMutations,
        selectComparatorOrder,
        selectSearchQueryRegex,
    ], (comparator, queryListFilter, queryList, mutationsList, isAscending, searchRegex) => {
        const filteredList = queryListFilter(searchRegex, queryList.concat(mutationsList));
        const computedComparator = isAscending
            ? comparator
            : flipComparator(comparator);
        return filteredList.slice().sort(computedComparator);
    });
    const selectCurrentQueryInfo = createSelector(selectAllQueries, selectAllMutations, ({ monitorState }) => monitorState.selectedQueryKey, (allQueries, allMutations, selectedQueryKey) => {
        if (!selectedQueryKey) {
            return null;
        }
        let currentQueryInfo = allQueries.find((query) => query.queryKey === selectedQueryKey.queryKey &&
            selectedQueryKey.reducerPath === query.reducerPath) || null;
        if (!currentQueryInfo) {
            currentQueryInfo =
                allMutations.find((mutation) => mutation.queryKey === selectedQueryKey.queryKey &&
                    selectedQueryKey.reducerPath === mutation.reducerPath) || null;
        }
        return currentQueryInfo;
    });
    const selectApiOfCurrentQuery = (selectorsSource) => {
        const apiStates = selectApiStates(selectorsSource);
        const currentQueryInfo = selectCurrentQueryInfo(selectorsSource);
        if (!apiStates || !currentQueryInfo) {
            return null;
        }
        return apiStates[currentQueryInfo.reducerPath] ?? null;
    };
    const selectProvidedOfCurrentQuery = (selectorsSource) => {
        return selectApiOfCurrentQuery(selectorsSource)?.provided ?? null;
    };
    const selectSubscriptionsOfCurrentQuery = createSelector([selectApiOfCurrentQuery, selectCurrentQueryInfo], (apiState, queryInfo) => {
        if (!queryInfo || !apiState) {
            return emptyRecord;
        }
        return apiState.subscriptions[queryInfo.queryKey];
    });
    const selectCurrentQueryTags = createSelector([selectCurrentQueryInfo, selectProvidedOfCurrentQuery], getQueryTagsOf);
    const selectApiStatsOfCurrentQuery = createSelector(selectApiOfCurrentQuery, (selectorsSource) => selectorsSource.actionsById, (selectorsSource) => selectorsSource.currentStateIndex, generateApiStatsOfCurrentQuery);
    const selectActionsOfCurrentQuery = createSelector(selectCurrentQueryInfo, selectActionsById, getActionsOfCurrentQuery);
    const selectTabCounters = createSelector([
        selectSubscriptionsOfCurrentQuery,
        selectActionsOfCurrentQuery,
        selectCurrentQueryTags,
    ], (subscriptions, actions, tags) => {
        return {
            [QueryPreviewTabs.data]: 0,
            [QueryPreviewTabs.queryTags]: tags.length,
            [QueryPreviewTabs.querySubscriptions]: Object.keys(subscriptions ?? {})
                .length,
            [QueryPreviewTabs.apiConfig]: 0,
            [QueryPreviewTabs.queryinfo]: 0,
            [QueryPreviewTabs.actions]: actions.length,
        };
    });
    return {
        selectQueryComparator,
        selectApiStates,
        selectAllQueries,
        selectAllVisbileQueries,
        selectSearchQueryRegex,
        selectCurrentQueryInfo,
        selectCurrentQueryTags,
        selectApiStatsOfCurrentQuery,
        selectSubscriptionsOfCurrentQuery,
        selectApiOfCurrentQuery,
        selectTabCounters,
        selectActionsOfCurrentQuery,
    };
}
